import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  RotateCcw, 
  Calendar, 
  Clock, 
  CheckSquare, 
  Plus, 
  User, 
  Building2, 
  X, 
  Eye,
  TrendingUp,
  BarChart3,
  CalendarRange,
  Filter,
  UserCheck,
  UserX
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { db, COLLECTIONS } from '../../firebase/config';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  doc, 
  updateDoc, 
  serverTimestamp, 
  addDoc, 
  deleteDoc,
  getDoc,
  setDoc 
} from 'firebase/firestore';

const DailyRoutinesWidget = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { createTask, tasks, users, departments, teams } = useData();
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personal'); // 'personal' or 'organizational'
  const [showExecutionStatusModal, setShowExecutionStatusModal] = useState(false);
  const [selectedRoutineForStatus, setSelectedRoutineForStatus] = useState(null);
  const [executionStatuses, setExecutionStatuses] = useState([]);
  const [allExecutionStatuses, setAllExecutionStatuses] = useState([]);
  
  useEffect(() => {
    loadRoutines();
  }, [currentUser]);

const isRoutineScheduledForToday = (routine) => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const dayOfMonth = today.getDate();
  const month = today.getMonth() + 1; // 1-12
  
  switch (routine.frequency || 'daily') {
    case 'daily':
      return true;
      
    case 'weekly':
      return dayOfWeek === (routine.weeklyDay || 1);
      
    case 'monthly':
      return dayOfMonth === (routine.monthlyDate || 1);
      
    case 'quarterly':
      const currentQuarter = Math.ceil(month / 3);
      const quarterStartMonth = (currentQuarter - 1) * 3 + 1;
      const targetMonth = quarterStartMonth + (routine.quarterlyMonth || 1) - 1;
      return month === targetMonth && dayOfMonth === (routine.quarterlyDay || 1);
      
    case 'yearly':
      return month === (routine.yearlyMonth || 1) && dayOfMonth === (routine.yearlyDay || 1);
      
    default:
      return true;
  }
};

  const loadRoutines = async () => {
    try {
      setLoading(true);
      const routinesRef = collection(db, COLLECTIONS.DAILY_ROUTINES || 'dailyRoutines');
      const snapshot = await getDocs(routinesRef);
      const allRoutines = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const filteredRoutines = allRoutines.filter(routine => {
        if (routine.scope === 'personal') {
          return routine.createdBy === currentUser.id;
        }
        
        // שגרות ניהוליות - מסנן לפי מחלקה
        if (routine.scope === 'managerial') {
          // מנהלים בכירים רואים הכל
          if (currentUser.role === 'ceo' || currentUser.role === 'deputy_ceo' || currentUser.role === 'org_admin') {
            return true;
          }
          
          // אם השגרה מיועדת לכל המחלקות, כל העובדים רואים אותה
          if (routine.departmentId === 'all') {
            return true;
          }
          
          // אחרים רואים רק שגרות של המחלקה שלהם
          return routine.departmentId === currentUser.departmentId;
        }
        
        return false;
      });
      
      // סינון רק שגרות פעילות ומתוזמנות להיום
      const activeRoutines = filteredRoutines.filter(r => r.isActive && isRoutineScheduledForToday(r));
      
      setRoutines(activeRoutines);
    } catch (error) {
      console.error('Error loading routines:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get routine executions for a specific date
  const getRoutineExecutions = async (routineId, date) => {
    try {
      const executionsRef = collection(db, 'routineExecutions');
      const q = query(
        executionsRef, 
        where('routineId', '==', routineId),
        where('executionDate', '==', date)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting executions:', error);
      return [];
    }
  };

  // Check if routine is completed today
  const isRoutineCompletedToday = async (routine) => {
    const today = new Date().toISOString().split('T')[0];
    const executions = await getRoutineExecutions(routine.id, today);
    
    // Get all assignees
    const assigneeIds = routine.assigneeIds || [routine.assigneeId];
    
    // Check if all assignees completed
    if (assigneeIds.length > 0 && assigneeIds[0]) {
      return assigneeIds.every(assigneeId => 
        executions.some(exec => exec.userId === assigneeId)
      );
    } else {
      // אם אין אחראים ספציפיים, בדוק אם היוצר השלים
      return executions.some(exec => exec.userId === routine.createdBy);
    }
  };

  // Load execution status for a routine
  const loadExecutionStatus = async (routine) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const executions = await getRoutineExecutions(routine.id, today);
      
      // קבע אילו משתמשים רלוונטיים לשגרה
      let relevantUsers = [];

      if (routine.scope === 'managerial') {
        // שגרה ארגונית - כל העובדים הפעילים
        if (routine.departmentId && routine.departmentId !== 'all') {
          // אם יש מחלקה ספציפית - רק עובדי המחלקה
          relevantUsers = users.filter(u => 
            u.isActive && u.departmentId === routine.departmentId
          );
        } else {
          // אם אין מחלקה או נבחר "כל המחלקות" - כל העובדים הפעילים
          relevantUsers = users.filter(u => u.isActive);
        }
      } else {
        // שגרה אישית - רק האחראים שנבחרו או היוצר אם לא נבחרו אחראים
        const assigneeIds = routine.assigneeIds || [routine.assigneeId];
        if (assigneeIds.length > 0 && assigneeIds[0]) {
          relevantUsers = assigneeIds.map(id => users.find(u => u.id === id)).filter(Boolean);
        } else {
          // אם לא נבחרו אחראים ספציפיים, השגרה חלה רק על היוצר
          relevantUsers = users.filter(u => u.id === routine.createdBy);
        }
      }

      // צור סטטוס לכל משתמש רלוונטי
      const statuses = [];
      for (const user of relevantUsers) {
        const execution = executions.find(e => e.userId === user.id);
        
        statuses.push({
          userId: user.id,
          userName: user.name,
          departmentId: user.departmentId,
          department: departments.find(d => d.id === user.departmentId)?.name,
          teamId: user.teamId,
          team: teams.find(t => t.id === user.teamId)?.name,
          status: execution ? 'completed' : 'pending',
          completedAt: execution?.completedAt?.toDate?.() || null,
          completedBy: execution?.completedBy
        });
      }
      
      setExecutionStatuses(statuses);
      setAllExecutionStatuses(statuses);
    } catch (error) {
      console.error('Error loading execution status:', error);
    }
  };

  // Helper function to check if user can view routine status
  const canViewRoutineStatus = (routine) => {
    // Personal routines - only creator and assignees can view
    if (routine.scope === 'personal') {
      const assigneeIds = routine.assigneeIds || [routine.assigneeId];
      if (assigneeIds.length > 0 && assigneeIds[0]) {
        return routine.createdBy === currentUser.id || assigneeIds.includes(currentUser.id);
      } else {
        // אם אין אחראים ספציפיים, רק היוצר יכול לראות
        return routine.createdBy === currentUser.id;
      }
    }
    
    // Organizational routines - based on role
    if (routine.scope === 'managerial') {
      // CEO and Deputy CEO can view all organizational routines
      if (currentUser.role === 'ceo' || currentUser.role === 'deputy_ceo') {
        return true;
      }
      
      // Department manager can view routines of their department
      if (currentUser.role === 'dept_manager') {
        return routine.departmentId === currentUser.departmentId || routine.departmentId === 'all';
      }
      
      // Team manager can view routines of their team
      if (currentUser.role === 'team_manager') {
        return routine.teamId === currentUser.teamId || 
               (routine.departmentId === currentUser.departmentId && !routine.teamId) ||
               routine.departmentId === 'all';
      }
      
      // Org admin can view all
      if (currentUser.role === 'org_admin') {
        return true;
      }
      
      // Employees can only view if they are assigned or if no specific assignees
      const assigneeIds = routine.assigneeIds || [routine.assigneeId];
      if (assigneeIds.length > 0 && assigneeIds[0]) {
        return assigneeIds.includes(currentUser.id);
      } else {
        // אם אין אחראים ספציפיים, כל העובדים יכולים לראות
        return true;
      }
    }
    
    return false;
  };

  // Open execution status modal
  const openExecutionStatusModal = async (routine) => {
    // Check permissions before opening modal
    if (!canViewRoutineStatus(routine)) {
      alert('אין לך הרשאות לצפות בסטטוס של שגרה זו');
      return;
    }
    
    setSelectedRoutineForStatus(routine);
    await loadExecutionStatus(routine);
    setShowExecutionStatusModal(true);
  };

  // Helper function to check if user can undo routine completion for another user
  const canUndoRoutineCompletion = (routine, targetUserId) => {
    // User can always undo their own completion
    if (currentUser.id === targetUserId) {
      return true;
    }
    
    // Personal routines - only creator can undo for others
    if (routine.scope === 'personal') {
      return routine.createdBy === currentUser.id;
    }
    
    // Organizational routines - based on role hierarchy
    if (routine.scope === 'managerial') {
      // CEO and Deputy CEO can undo for anyone
      if (currentUser.role === 'ceo' || currentUser.role === 'deputy_ceo') {
        return true;
      }
      
      // Org admin can undo for anyone
      if (currentUser.role === 'org_admin') {
        return true;
      }
      
      const targetUser = users.find(u => u.id === targetUserId);
      if (!targetUser) return false;
      
      // Department manager can undo for their department members
      if (currentUser.role === 'dept_manager') {
        return targetUser.departmentId === currentUser.departmentId;
      }
      
      // Team manager can undo for their team members
      if (currentUser.role === 'team_manager') {
        return targetUser.teamId === currentUser.teamId;
      }
    }
    
    return false;
  };

  // Mark routine as completed by specific user
  const markRoutineAsCompleted = async (routine, userId) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const executionRef = doc(collection(db, 'routineExecutions'));
      
      await setDoc(executionRef, {
        routineId: routine.id,
        userId: userId,
        executionDate: today,
        completedAt: serverTimestamp(),
        completedBy: currentUser.id,
        routineName: routine.name
      });
      
      // Create task for tracking
      const user = users.find(u => u.id === userId);
      const taskTitle = `${routine.name} - ${user?.name} - ${new Date().toLocaleDateString('he-IL')}`;
      
      await createTask({
        title: taskTitle,
        description: routine.description,
        assigneeId: userId,
        assigneeIds: [userId],
        departmentId: routine.departmentId,
        teamId: routine.teamId,
        priority: routine.priority,
        estimate: routine.estimatedTime / 60,
        dueDate: today,
        status: 'הושלם',
        scope: routine.scope || 'managerial',
        isDailyRoutine: true,
        routineId: routine.id,
        createdFrom: 'daily_routine',
        completedAt: new Date()
      });
      
      // Reload execution status
      await loadExecutionStatus(routine);
      await loadRoutines();
      
      alert(`השגרה סומנה כהושלמה עבור ${user?.name}!`);
    } catch (error) {
      console.error('Error marking routine as completed:', error);
      alert('שגיאה בסימון השגרה כהושלמה');
    }
  };

  // Undo routine completion for specific user
  const undoRoutineCompletion = async (routine, userId) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Find and delete execution record
      const executionsRef = collection(db, 'routineExecutions');
      const q = query(
        executionsRef,
        where('routineId', '==', routine.id),
        where('userId', '==', userId),
        where('executionDate', '==', today)
      );
      const snapshot = await getDocs(q);
      
      for (const doc of snapshot.docs) {
        await deleteDoc(doc.ref);
      }
      
      // Delete the task
      const completedTask = tasks.find(task => 
        task.routineId === routine.id && 
        task.assigneeId === userId &&
        task.dueDate === today && 
        task.status === 'הושלם' &&
        task.isDailyRoutine
      );
      
      if (completedTask) {
        await deleteDoc(doc(db, COLLECTIONS.TASKS || 'tasks', completedTask.id));
      }
      
      // Reload execution status
      await loadExecutionStatus(routine);
      await loadRoutines();
      
      alert('ביטול הסימון בוצע בהצלחה!');
    } catch (error) {
      console.error('Error undoing completion:', error);
      alert('שגיאה בביטול הסימון');
    }
  };

const getFrequencyLabel = (frequency) => {
  switch (frequency || 'daily') {
    case 'daily': return 'יומי';
    case 'weekly': return 'שבועי';
    case 'monthly': return 'חודשי';
    case 'quarterly': return 'רבעוני';
    case 'yearly': return 'שנתי';
    default: return 'יומי';
  }
};

  // הפרדת שגרות לפי scope
  const personalRoutines = routines.filter(r => r.scope === 'personal');
  const organizationalRoutines = routines.filter(r => r.scope === 'managerial');

  // חישוב סטטיסטיקות
  const getTabStats = (routinesList) => {
    const totalTime = routinesList.reduce((sum, r) => sum + (r.estimatedTime || 0), 0);
    const totalAssignees = routinesList.reduce((sum, r) => {
      const assigneeCount = r.assigneeIds ? r.assigneeIds.length : 1;
      return sum + assigneeCount;
    }, 0);
    
    return { 
      totalTime, 
      totalAssignees,
      routineCount: routinesList.length 
    };
  };

  const personalStats = getTabStats(personalRoutines);
  const organizationalStats = getTabStats(organizationalRoutines);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const renderRoutinesList = (routinesList) => {
    if (routinesList.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <RotateCcw className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-lg font-medium">אין שגרות מתוזמנות להיום</p>
          <p className="text-sm">צור שגרה חדשה או בדוק את התזמון של השגרות הקיימות</p>
        </div>
      );
    }

    return (
      <>
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-600">שגרות להיום</p>
            <p className="text-xl font-bold text-gray-900">{routinesList.length}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-gray-600">משתתפים</p>
            <p className="text-xl font-bold text-blue-600">
              {routinesList.reduce((sum, r) => sum + (r.assigneeIds?.length || 1), 0)}
            </p>
          </div>
          <div className="bg-purple-50 rounded-lg p-3">
            <p className="text-xs text-gray-600">זמן כולל</p>
            <p className="text-xl font-bold text-purple-600">
              {routinesList.reduce((sum, r) => sum + (r.estimatedTime || 0), 0)} דק'
            </p>
          </div>
        </div>

        {/* Routines List */}
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {routinesList.map(routine => {
            const assigneeIds = routine.assigneeIds || [routine.assigneeId];
            const assignees = assigneeIds.length > 0 && assigneeIds[0] 
              ? assigneeIds.map(id => users.find(u => u.id === id)).filter(Boolean)
              : [users.find(u => u.id === routine.createdBy)].filter(Boolean);
            
            return (
              <div 
                key={routine.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-green-400 to-blue-500`}>
                    <RotateCcw className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-sm text-gray-900">{routine.name}</h4>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {routine.estimatedTime} דקות
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {routine.dueTime}
                      </span>
                      <span className="flex items-center gap-1">
                        <CalendarRange className="w-3 h-3" />
                        {getFrequencyLabel(routine.frequency)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {assignees.length} אחראים
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canViewRoutineStatus(routine) ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openExecutionStatusModal(routine);
                      }}
                      className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" />
                      סטטוס ביצוע
                    </button>
                  ) : (
                    <div className="px-3 py-1 bg-gray-100 text-gray-500 rounded-lg text-sm font-medium flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      אין הרשאה
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/daily-routines');
                    }}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    פרטים ←
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg flex items-center justify-center">
            <RotateCcw className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">שגרות להיום</h2>
            <p className="text-sm text-gray-600">משימות שחוזרות על עצמן</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/daily-routines')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
          >
            <RotateCcw className="w-4 h-4 flex-shrink-0" />
            נהל שגרות
          </button>
          <button
            onClick={() => navigate('/daily-routines?action=create')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm font-medium"
          >
            <Plus className="w-4 h-4 flex-shrink-0" />
            צור שגרה
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => setActiveTab('personal')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'personal'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <User className="w-4 h-4" />
          שגרות אישיות ({personalRoutines.length})
        </button>
        <button
          onClick={() => setActiveTab('organizational')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'organizational'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Building2 className="w-4 h-4" />
          שגרות ארגוניות ({organizationalRoutines.length})
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'personal' && renderRoutinesList(personalRoutines)}
      {activeTab === 'organizational' && renderRoutinesList(organizationalRoutines)}

      {/* Execution Status Modal */}
      {showExecutionStatusModal && selectedRoutineForStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">סטטוס ביצוע - {selectedRoutineForStatus.name}</h2>
                  <p className="text-indigo-100 text-sm mt-1">
                    תאריך: {new Date().toLocaleDateString('he-IL')} | 
                    תדירות: {getFrequencyLabel(selectedRoutineForStatus.frequency)} |
                    {selectedRoutineForStatus.scope === 'managerial' 
                      ? `שגרה ארגונית - ${executionStatuses.length} עובדים`
                      : 'שגרה אישית'
                    }
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowExecutionStatusModal(false);
                    setSelectedRoutineForStatus(null);
                  }}
                  className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="p-4 bg-gray-50 border-b">
              <div className="flex gap-4 items-center">
                <Filter className="w-4 h-4 text-gray-500" />
                <select 
                  className="px-3 py-1 border rounded-lg text-sm"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'all') {
                      setExecutionStatuses(allExecutionStatuses);
                    } else {
                      const filtered = allExecutionStatuses.filter(s => s.status === value);
                      setExecutionStatuses(filtered);
                    }
                  }}
                >
                  <option value="all">כל הסטטוסים</option>
                  <option value="completed">ביצעו שגרה</option>
                  <option value="pending">לא ביצעו שגרה</option>
                </select>
              </div>
            </div>

            {/* Status List */}
            <div className="p-4">
              <div className="space-y-2">
                {executionStatuses.map((status) => (
                  <div key={status.userId} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        status.status === 'completed' ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        {status.status === 'completed' ? (
                          <UserCheck className="w-4 h-4 text-green-600" />
                        ) : (
                          <UserX className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{status.userName}</div>
                        {status.department && (
                          <div className="text-xs text-gray-500">{status.department}</div>
                        )}
                        {status.status === 'completed' && status.completedAt && (
                          <div className="text-xs text-green-600">
                            הושלם בשעה {status.completedAt.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      status.status === 'completed' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {status.status === 'completed' ? 'ביצע שגרה' : 'לא ביצע שגרה'}
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex justify-around text-center">
                  <div>
                    <p className="text-lg font-bold text-green-600">
                      {executionStatuses.filter(s => s.status === 'completed').length}
                    </p>
                    <p className="text-xs text-gray-600">ביצעו</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-orange-600">
                      {executionStatuses.filter(s => s.status === 'pending').length}
                    </p>
                    <p className="text-xs text-gray-600">לא ביצעו</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-blue-600">
                      {executionStatuses.length > 0 
                        ? Math.round((executionStatuses.filter(s => s.status === 'completed').length / executionStatuses.length) * 100)
                        : 0}%
                    </p>
                    <p className="text-xs text-gray-600">השלמה</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyRoutinesWidget;